import axios from 'axios';
import { syncStateManager, userGeneratedExamDB, updateToken } from '..';
import { tlLogger } from '@taleemabad/shared';
import { ICachedGeneratedExam } from '../models/user-generated-exam';
import {
  ExamSubject,
  IBackendExamJson,
  IExamQuestionPath,
  QuestionCategory,
  UGExamQuestionType,
  ExamStatus,
  IBackendGeneratedExam,
  IBackendQuestion,
} from '../types/user-generated-exam';
import { userCache } from '../utils/user-cache';

// Raw axios instance for UG EG API - bypasses axios-case-converter
// to preserve format type keys in exam_json (e.g., "MCQs", "Fill in the blanks")
const ugEgApiService = axios.create({ timeout: 180000 });

ugEgApiService.interceptors.request.use(async (config) => {
  config.baseURL =
    (await userCache.getBaseUrl()) ?? process.env.NX_PUBLIC_BASE_URI;
  const token = userCache.getTokenFromCache();
  if (token?.access) {
    config.headers.setAuthorization(`Bearer ${token.access}`);
  }
  return config;
});

ugEgApiService.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;
    const isUserCacheAvailable = userCache.getTokenFromCache();
    if (
      error.response?.status === 401 &&
      isUserCacheAvailable &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      return updateToken().then((data) => {
        if (data?.access) {
          error.config.headers['Authorization'] = `Bearer ${data.access}`;
          return axios.request(originalRequest);
        }
      });
    }
    return Promise.reject(error);
  },
);

export interface IRawApiResponse {
  id: number | string;
  status: ExamStatus;
  exam_json: IBackendExamJson;
  exam_html: string;
  tags: {
    grade?: number;
    subject?: ExamSubject;
    page_ranges?: string;
    question_types?: QuestionCategory[];
    seen_categories?: UGExamQuestionType[] | null;
    unseen_categories?: UGExamQuestionType[] | null;
    unseen_objective_types?: string[] | null;
    unseen_subjective_types?: string[] | null;
    exam_type?: string;
    selected_chapters?: number[];
    image_generation_enabled?: boolean;
  };
  metadata: Record<string, unknown>;
  created: string;
  modified: string;
  selected_question_ids?: string[] | null;
}

/**
 * Convert raw snake_case backend response to IBackendGeneratedExam
 * while preserving exam_json format type keys as-is from the backend.
 * Exported for unit testing — do NOT run through axios-case-converter.
 */
export function mapRawResponse(raw: IRawApiResponse): IBackendGeneratedExam {
  return {
    id: String(raw.id),
    status: raw.status,
    examJson: raw.exam_json || {},
    examHtml: raw.exam_html || '',
    tags: {
      grade: raw.tags?.grade ?? 0,
      subject: raw.tags?.subject ?? ExamSubject.English,
      pageRanges: raw.tags?.page_ranges ?? '',
      questionTypes: raw.tags?.question_types ?? [],
      seenCategories: raw.tags?.seen_categories,
      unseenCategories: raw.tags?.unseen_categories,
      unseenObjectiveTypes: raw.tags?.unseen_objective_types,
      unseenSubjectiveTypes: raw.tags?.unseen_subjective_types,
      examType: raw.tags?.exam_type,
      selectedChapters: raw.tags?.selected_chapters,
      imageGenerationEnabled: raw.tags?.image_generation_enabled,
    },
    metadata: raw.metadata || {},
    created: raw.created,
    modified: raw.modified,
    selectedQuestionIds: raw.selected_question_ids ?? null,
  };
}

export interface IGenerateExamRequest {
  grade: number;
  subject: ExamSubject;
  pageRanges: string;
  questionTypes: QuestionCategory[];
  seenCategories?: UGExamQuestionType[] | null;
  unseenCategories?: UGExamQuestionType[] | null;
  unseenObjectiveTypes?: string[] | null;
  unseenSubjectiveTypes?: string[] | null;
  unseenObjectiveCounts?: Record<string, number>;
  unseenSubjectiveCounts?: Record<string, number>;
  examType?: string;
  selectedChapters?: number[];
  imageGenerationEnabled?: boolean;
}

interface IBackendExamRequest {
  grade: number;
  subject: ExamSubject;
  page_ranges: string;
  question_types: QuestionCategory[];
  seen_categories?: UGExamQuestionType[] | null;
  unseen_categories?: UGExamQuestionType[] | null;
  unseen_objective_types?: string[] | null;
  unseen_subjective_types?: string[] | null;
  unseen_objective_counts?: Record<string, number>;
  unseen_subjective_counts?: Record<string, number>;
  exam_type?: string;
  selected_chapters?: number[];
  image_generation_enabled?: boolean;
}

/**
 * Convert camelCase request body to snake_case for the backend
 */
function toSnakeCaseRequest(
  request: IGenerateExamRequest,
): IBackendExamRequest {
  return {
    grade: request.grade,
    subject: request.subject,
    page_ranges: request.pageRanges,
    question_types: request.questionTypes,
    seen_categories: request.seenCategories,
    unseen_categories: request.unseenCategories,
    unseen_objective_types: request.unseenObjectiveTypes,
    unseen_subjective_types: request.unseenSubjectiveTypes,
    unseen_objective_counts: request.unseenObjectiveCounts,
    unseen_subjective_counts: request.unseenSubjectiveCounts,
    exam_type: request.examType,
    selected_chapters: request.selectedChapters,
    image_generation_enabled: request.imageGenerationEnabled,
  };
}

// Response interface (matches GeneratedExamSerializer)
export interface IGeneratedExam {
  id: string;
  status: ExamStatus;
  examJson: {
    questions: IExamQuestion[];
    metadata?: Record<string, any>;
  };
  rawExamJson?: IBackendExamJson;
  examHtml: string;
  tags: IGenerateExamRequest;
  metadata: Record<string, any>;
  created: string;
  modified: string;
  selectedQuestionIds?: string[] | null;
}

export interface IExamSubQuestion {
  text: string;
  marks: number;
  lines: number;
}

export interface IExamQuestion {
  id: string;
  question: string;
  marks: number;
  blooms?: string;
  category: QuestionCategory;
  type: UGExamQuestionType;
  formatType: string;
  mainQuestion?: string;
  answerLines?: number;
  subQuestions?: IExamSubQuestion[];
  options?: string[];
  rawQuestion: string;
  image?: string;
  _sourcePath: IExamQuestionPath;
  column_a?: string[];
  column_b?: string[];
}

const EXAM_CATEGORIES: QuestionCategory[] = [
  QuestionCategory.Seen,
  QuestionCategory.Unseen,
];

const EXAM_TYPES: UGExamQuestionType[] = [
  UGExamQuestionType.Objective,
  UGExamQuestionType.Subjective,
];

function buildPassageText(q: IBackendQuestion): string {
  const passageText = q.passage ?? '';
  const sectionText = q.section ? `[${q.section}]` : '';
  if (passageText && sectionText) return `${passageText}\n\n${sectionText}`;
  return passageText || sectionText;
}

function buildColumnText(q: IBackendQuestion): string {
  const header = q.question ? q.question + '\n\n' : '';
  const colA =
    'Column A:\n' + (q.column_a ?? []).map((item) => `  ${item}`).join('\n');
  const colB =
    'Column B:\n' + (q.column_b ?? []).map((item) => `  ${item}`).join('\n');
  return header + colA + '\n\n' + colB;
}

function buildQuestionText(q: IBackendQuestion): string {
  if (q.words?.length) {
    return '';
  }
  if (q.column_a && q.column_b) {
    return buildColumnText(q);
  }
  if (q.passage !== undefined || q.questions?.length) {
    return buildPassageText(q);
  }
  if (q.question) {
    return q.question;
  }
  return '';
}

function buildSubQuestions(
  q: IBackendQuestion,
): IExamSubQuestion[] | undefined {
  if (q.words?.length) {
    const marksEach = q.words.length > 0 ? q.marks / q.words.length : 0;
    return q.words.map((word) => ({
      text: word,
      marks: marksEach,
      lines: q.lines ?? 1,
    }));
  }
  if (!q.questions?.length) return undefined;
  return q.questions.map((subQ) => ({
    text: subQ.question,
    marks: subQ.marks,
    lines: subQ.lines ?? 0,
  }));
}

function processTypeData(
  typeData: Record<string, IBackendQuestion[] | undefined>,
  category: QuestionCategory,
  type: UGExamQuestionType,
  questions: IExamQuestion[],
  counter: { value: number },
): void {
  for (const [formatType, questionsList] of Object.entries(typeData)) {
    if (!Array.isArray(questionsList) || questionsList.length === 0) continue;
    const mainQuestion = questionsList[0]?.main_question;
    for (const [qIdx, q] of questionsList.entries()) {
      questions.push({
        id: `q-${counter.value++}`,
        question: buildQuestionText(q),
        marks: q.marks,
        blooms: q.blooms,
        category,
        type,
        formatType,
        mainQuestion,
        answerLines: q.lines ?? 0,
        subQuestions: buildSubQuestions(q),
        options: q.options?.length ? q.options : undefined,
        rawQuestion: q.question ?? '',
        image: q.image,
        _sourcePath: { category, type, formatType, index: qIdx },
        column_a: q.column_a,
        column_b: q.column_b,
      });
    }
  }
}

export function transformBackendExamJson(backendExamJson: IBackendExamJson): {
  questions: IExamQuestion[];
} {
  const questions: IExamQuestion[] = [];
  const counter = { value: 1 };

  for (const category of EXAM_CATEGORIES) {
    const categoryData = backendExamJson[category];
    if (!categoryData) continue;

    for (const type of EXAM_TYPES) {
      const typeData = categoryData[type];
      if (typeData) {
        processTypeData(typeData, category, type, questions, counter);
      }
    }
  }

  return { questions };
}

export function applyEditToRawExamJson(
  rawExamJson: IBackendExamJson,
  path: IExamQuestionPath,
  newText: string,
  newMarks: number,
  newOptions?: string[],
  newSubQuestions?: Array<{ text: string; marks: number }>,
  subQuestionSource?: string,
  editPassage?: boolean,
): IBackendExamJson {
  const updated: IBackendExamJson = JSON.parse(JSON.stringify(rawExamJson));
  const categoryData = updated[path.category];
  if (!categoryData)
    throw new Error(`Invalid path: category '${path.category}' not found`);
  const typeData = categoryData[path.type];
  if (!typeData) throw new Error(`Invalid path: type '${path.type}' not found`);
  const questionsList = typeData[path.formatType];
  if (!Array.isArray(questionsList) || !questionsList[path.index]) {
    throw new Error(
      `Invalid path: formatType '${path.formatType}' index ${path.index} not found`,
    );
  }

  if (editPassage) {
    questionsList[path.index].passage = newText;
  } else {
    questionsList[path.index].question = newText;
  }
  questionsList[path.index].marks = newMarks;
  if (newOptions !== undefined) {
    questionsList[path.index].options = newOptions;
  }
  if (newSubQuestions !== undefined) {
    const rawQ = questionsList[path.index] as unknown as Record<
      string,
      unknown
    >;
    const resolvedSource =
      subQuestionSource ??
      (Array.isArray(rawQ['words']) ? 'words' : 'questions');
    const existing = rawQ[resolvedSource];
    if (Array.isArray(existing)) {
      if (existing.length === 0 || typeof existing[0] === 'string') {
        rawQ[resolvedSource] = newSubQuestions.map(
          (sq, i) => sq.text ?? (existing as string[])[i],
        );
      } else {
        rawQ[resolvedSource] = (existing as Array<Record<string, unknown>>).map(
          (sq, i) => ({
            ...sq,
            question: newSubQuestions[i]?.text ?? sq['question'],
            marks: newSubQuestions[i]?.marks ?? sq['marks'],
          }),
        );
      }
    }
  }
  return updated;
}

class UserGeneratedExamAPI {
  private readonly apiV1 = '/api/v1/ug-eg';

  private transformBackendResponse(
    backendData: IBackendGeneratedExam,
  ): IGeneratedExam {
    return {
      ...backendData,
      id: String(backendData.id), // Ensure ID is always a string
      examJson: transformBackendExamJson(backendData.examJson || {}),
      rawExamJson: backendData.examJson,
    };
  }

  /**
   * Generate a new exam (online-only operation)
   */
  async generateExam(request: IGenerateExamRequest): Promise<IGeneratedExam> {
    try {
      tlLogger.log('UGEG:GenerateExam:Start', { request });

      const response = await ugEgApiService.post(
        `${this.apiV1}/generate-exam/`,
        toSnakeCaseRequest(request),
      );

      // Transform backend response to frontend format
      const transformedExam = this.transformBackendResponse(
        mapRawResponse(response.data),
      );

      // Cache the initial response (status=pending)
      await this.cacheSingleExam(transformedExam);

      tlLogger.log('UGEG:GenerateExam:Success', {
        examId: transformedExam.id,
      });
      return transformedExam;
    } catch (error: any) {
      tlLogger.error('UGEG:GenerateExam:Failed', {
        error: error.response?.data || error.message,
      });
      throw error;
    }
  }

  /**
   * Get exam by ID with offline fallback
   * Strategy: Always check cache first, then fetch online if needed
   */
  async getGeneratedExam(examId: string | number): Promise<IGeneratedExam> {
    // Ensure examId is always a string for consistent lookup
    const examIdStr = String(examId);

    // Always try cache first
    try {
      const cachedExam = await userGeneratedExamDB.getCachedExamById(examIdStr);

      if (cachedExam) {
        tlLogger.log('UGEG:GetExam:FromCache', {
          examId: examIdStr,
          isOnline: syncStateManager.isConnected,
          status: cachedExam.status,
        });

        // If offline, must use cache
        if (!syncStateManager.isConnected) {
          return this.mapCachedToApiResponse(cachedExam);
        }

        // If online, use cache but refresh in background
        this.refreshExamInBackground(examIdStr, cachedExam);
        return this.mapCachedToApiResponse(cachedExam);
      }
    } catch (cacheError) {
      tlLogger.error('UGEG:GetExam:CacheError', {
        examId: examIdStr,
        cacheError,
      });
    }

    // Not in cache - must be online to proceed
    if (!syncStateManager.isConnected) {
      const error = new Error('Exam not available offline');
      tlLogger.error('UGEG:GetExam:OfflineNotCached', { examId: examIdStr });
      throw error;
    }

    // Online: Fetch from API
    try {
      tlLogger.log('UGEG:GetExam:FetchingAPI', { examId: examIdStr });
      const response = await ugEgApiService.get(
        `${this.apiV1}/generated-exams/${examIdStr}/`,
      );

      const transformedExam = this.transformBackendResponse(
        mapRawResponse(response.data),
      );
      await this.cacheSingleExam(transformedExam);

      return transformedExam;
    } catch (error: any) {
      tlLogger.error('UGEG:GetExam:Failed', {
        examId: examIdStr,
        error: error.response?.data || error.message,
      });
      throw error;
    }
  }

  async updateGeneratedExam(
    examId: string,
    rawExamJson: IBackendExamJson,
  ): Promise<IGeneratedExam> {
    if (!syncStateManager.isConnected) {
      throw new Error('Cannot save question edits while offline.');
    }

    try {
      tlLogger.log('UGEG:UpdateExam:Start', { examId });

      const response = await ugEgApiService.patch(
        `${this.apiV1}/generated-exams/${examId}/`,
        { exam_json: rawExamJson },
      );

      const transformedExam = this.transformBackendResponse(
        mapRawResponse(response.data),
      );

      await this.cacheSingleExam(transformedExam);

      tlLogger.log('UGEG:UpdateExam:Success', { examId });
      return transformedExam;
    } catch (error: any) {
      tlLogger.error('UGEG:UpdateExam:Failed', {
        examId,
        error: error.response?.data || error.message,
      });
      throw error;
    }
  }

  async deleteGeneratedExam(examId: string): Promise<void> {
    try {
      tlLogger.log('UGEG:DeleteExam:Start', { examId });

      await ugEgApiService.delete(`${this.apiV1}/generated-exams/${examId}/`);

      await userGeneratedExamDB.deleteExam(examId);

      tlLogger.log('UGEG:DeleteExam:Success', { examId });
    } catch (error: unknown) {
      tlLogger.error('UGEG:DeleteExam:Failed', {
        examId,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
  }

  /**
   * Persist selected question IDs to backend (best-effort — does not throw on
   * network failure so callers can fire-and-forget via .catch()).
   */
  async patchSelectedQuestionIds(
    examId: string,
    selectedQuestionIds: string[] | null,
  ): Promise<IGeneratedExam> {
    try {
      tlLogger.log('UGEG:PatchSelectedIds:Start', { examId });

      const response = await ugEgApiService.patch(
        `${this.apiV1}/generated-exams/${examId}/`,
        { selected_question_ids: selectedQuestionIds },
      );

      const transformedExam = this.transformBackendResponse(
        mapRawResponse(response.data),
      );

      await this.cacheSingleExam(transformedExam);

      tlLogger.log('UGEG:PatchSelectedIds:Success', { examId });
      return transformedExam;
    } catch (err: any) {
      tlLogger.error('UGEG:PatchSelectedIds:Failed', {
        examId,
        error: err.response?.data || err.message,
      });
      throw err;
    }
  }

  /**
   * Refresh exam in background if cache is stale
   */
  private refreshExamInBackground(
    examId: string,
    cachedExam: ICachedGeneratedExam,
  ): void {
    const cacheAge = Date.now() - cachedExam.cachedAt;
    // Only refresh if cached more than 30 seconds ago
    if (cacheAge < 30000) return;

    ugEgApiService
      .get(`${this.apiV1}/generated-exams/${examId}/`)
      .then((response) => {
        const transformedExam = this.transformBackendResponse(
          mapRawResponse(response.data),
        );
        this.cacheSingleExam(transformedExam);
        tlLogger.log('UGEG:GetExam:BackgroundRefresh', { examId });
      })
      .catch((error) => {
        // Silent fail - we already have cached data
        tlLogger.log('UGEG:GetExam:BackgroundRefreshFailed', {
          examId,
          error: error.message,
        });
      });
  }

  /**
   * List all user's generated exams with offline fallback
   */
  async listGeneratedExams(): Promise<IGeneratedExam[]> {
    try {
      // If offline, return cached data
      if (!syncStateManager.isConnected) {
        tlLogger.log('UGEG:ListExams:Offline', {});
        const cachedExams = await this.getCachedExams();
        return cachedExams;
      }

      // Online: Fetch from API
      tlLogger.log('UGEG:ListExams:FetchingAPI', {});
      const response = await ugEgApiService.get(
        `${this.apiV1}/generated-exams/`,
      );

      // Transform backend responses to frontend format
      const transformedExams = (response.data as IRawApiResponse[]).map(
        (exam) => this.transformBackendResponse(mapRawResponse(exam)),
      );

      // Cache the response data for offline use
      await this.cacheResponseData(transformedExams);

      return transformedExams;
    } catch (error: any) {
      // If online request fails, try cache as fallback
      if (syncStateManager.isConnected) {
        try {
          return await this.getCachedExams();
        } catch (cacheError) {
          tlLogger.error('UGEG:ListExams:CacheFallbackFailed', { cacheError });
        }
      }

      tlLogger.error('UGEG:ListExams:Failed', {
        error: error.response?.data || error.message,
      });
      throw error;
    }
  }

  // Helper method to get cached exams
  private async getCachedExams(): Promise<IGeneratedExam[]> {
    const cachedExams = await userGeneratedExamDB.getCachedExams();
    return cachedExams.map(this.mapCachedToApiResponse);
  }

  // Helper method to cache multiple exams
  private async cacheResponseData(exams: IGeneratedExam[]): Promise<void> {
    try {
      for (const exam of exams) {
        await this.cacheSingleExam(exam);
      }
    } catch (error) {
      tlLogger.error('UGEG:CacheResponseData:Failed', { error });
    }
  }

  // Helper method to cache a single exam (includes rawExamJson for PATCH support)
  private async cacheSingleExam(exam: IGeneratedExam): Promise<void> {
    try {
      const examIdStr = String(exam.id);
      const existing = await userGeneratedExamDB.getCachedExamById(examIdStr);

      if (existing) {
        const existingTs = existing.modified
          ? Date.parse(existing.modified)
          : 0;
        const incomingTs = exam.modified ? Date.parse(exam.modified) : 0;
        if (existingTs >= incomingTs) {
          // Incoming is not newer — skip overwrite to protect local edits
          tlLogger.log('UGEG:CacheSingleExam:SkippedStale', {
            examId: examIdStr,
            existingModified: existing.modified,
            incomingModified: exam.modified,
          });
          return;
        }
      }

      await userGeneratedExamDB.cacheExam({
        id: examIdStr,
        status: exam.status,
        examJson: exam.examJson,
        rawExamJson: exam.rawExamJson,
        examHtml: exam.examHtml,
        tags: exam.tags,
        metadata: exam.metadata,
        created: exam.created,
        modified: exam.modified,
        selectedQuestionIds: exam.selectedQuestionIds ?? null,
      });
      tlLogger.log('UGEG:CacheSingleExam:Success', { examId: exam.id });
    } catch (error) {
      tlLogger.error('UGEG:CacheSingleExam:Failed', { examId: exam.id, error });
    }
  }

  // Helper method to map cached data to API response format
  private mapCachedToApiResponse(cached: ICachedGeneratedExam): IGeneratedExam {
    // Re-run transform from rawExamJson so any buildPassageText changes apply
    // even on previously cached exams.
    const examJson = cached.rawExamJson
      ? transformBackendExamJson(cached.rawExamJson)
      : cached.examJson;
    return {
      id: cached.id,
      status: cached.status,
      examJson,
      rawExamJson: cached.rawExamJson,
      examHtml: cached.examHtml,
      tags: cached.tags,
      metadata: cached.metadata,
      created: cached.created,
      modified: cached.modified,
      selectedQuestionIds: cached.selectedQuestionIds ?? null,
    };
  }

  // Helper method to get offline status and cache stats
  async getOfflineStats(): Promise<{
    isOffline: boolean;
    cachedExams: { total: number; ready: number; pending: number };
  }> {
    const isOffline = !syncStateManager.isConnected;
    const cachedExams = await userGeneratedExamDB.getStorageStats();

    return { isOffline, cachedExams };
  }
}

export const userGeneratedExamAPI = new UserGeneratedExamAPI();

// Export legacy functions for backward compatibility
export const generateExam = (request: IGenerateExamRequest) =>
  userGeneratedExamAPI.generateExam(request);

export const getGeneratedExam = (examId: string) =>
  userGeneratedExamAPI.getGeneratedExam(examId);

export const listGeneratedExams = () =>
  userGeneratedExamAPI.listGeneratedExams();

export const updateGeneratedExam = (
  examId: string,
  rawExamJson: IBackendExamJson,
) => userGeneratedExamAPI.updateGeneratedExam(examId, rawExamJson);

export const patchSelectedQuestionIds = (
  examId: string,
  selectedQuestionIds: string[] | null,
) => userGeneratedExamAPI.patchSelectedQuestionIds(examId, selectedQuestionIds);

export const updateSelectionIds = (
  examId: string,
  selectedQuestionIds: string[] | null,
) => userGeneratedExamDB.updateSelectionIds(examId, selectedQuestionIds);

export const deleteGeneratedExam = (examId: string) =>
  userGeneratedExamAPI.deleteGeneratedExam(examId);
